import { useCallback, useEffect, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppSelector, useAppDispatch } from '../store/hooks';
import {
	setCredentials,
	completedSetup as completedSetupAction,
	clearCredentials,
} from '../store/authSlice';
import { useLogoutMutation } from '../store/api/authApi';
import { studentApi } from '../store/api/studentApi';
import { parentApi } from '../store/api/parentApi';
import { trainerApi } from '../store/api/trainerApi';
import { schoolAdminApi } from '../store/api/schoolAdminApi';
import { masterAdminApi } from '../store/api/masterAdminApi';
import { opsApi } from '../store/api/opsApi';
import { identifyUser, analytics, resetAnalytics } from '../services/analytics';
import type { AuthUser, AuthContextValue } from '../types/auth';
import type { AuthRole } from '../types/models';

// Set to true during logout so ProtectedRoute skips its redirect-to-'/' effect.
// Using a module-level variable avoids React state batching races.
let isLoggingOut = false;

/**
 * AuthProvider is now a thin wrapper — Redux Provider already manages state.
 * Kept for backwards compatibility so <AuthProvider> in App.tsx still compiles.
 */
export function AuthProvider({ children }: { children: ReactNode }) {
	return <>{children}</>;
}

/**
 * useAuth — reads auth state from Redux store and returns the same
 * AuthContextValue interface for full backwards compatibility.
 */
export function useAuth(): AuthContextValue {
	const dispatch = useAppDispatch();
	const navigate = useNavigate();
	const { user, role, isAuthenticated, mustChangePassword } = useAppSelector(
		(s) => s.auth,
	);
	const [logoutApi] = useLogoutMutation();

	const login = useCallback(
		(
			userData: AuthUser,
			userRole: AuthRole,
			accessToken: string,
			refreshToken: string,
			needsPasswordChange = false,
		) => {
			dispatch(
				setCredentials({
					user: userData,
					role: userRole,
					accessToken,
					refreshToken,
					mustChangePassword: needsPasswordChange,
				}),
			);
			// Identify with rich profile properties pulled from the login
			// response — no extra API calls. `identifyUser` also registers
			// super properties so every subsequent event carries role +
			// user_id + school_id automatically.
			identifyUser(userData, userRole);
			// Role-specific login event (student_login_succeeded, …) is fired
			// from the login page itself — it knows the login method (pin/face
			// for students) and can add method-specific context.
		},
		[dispatch],
	);

	const completedSetup = useCallback(() => {
		dispatch(completedSetupAction());
	}, [dispatch]);

	const logout = useCallback(async () => {
		const refreshToken = localStorage.getItem('alforaa_refresh_token');
		const loginRoutes: Record<AuthRole, string> = {
			student: '/student/login',
			parent: '/parent/login',
			trainer: '/trainer/login',
			school_admin: '/school/login',
			master_admin: '/admin/login',
			salesperson: '/sales/login',
			sales_manager: '/sales/login',
			appointment_coordinator: '/sales/login',
			zonal_manager: '/zonal/login',
		};
		// Capture the role before localStorage is cleared by clearCredentials
		const currentRole = role ?? (localStorage.getItem('alforaa_role') as AuthRole | null);
		const loginPath = currentRole ? (loginRoutes[currentRole] ?? '/student/login') : '/student/login';

		// Signal ProtectedRoute to skip its redirect while we handle navigation.
		isLoggingOut = true;
		navigate(loginPath, { replace: true });
		dispatch(clearCredentials());
		dispatch(studentApi.util.resetApiState());
		dispatch(parentApi.util.resetApiState());
		dispatch(trainerApi.util.resetApiState());
		dispatch(schoolAdminApi.util.resetApiState());
		dispatch(masterAdminApi.util.resetApiState());
		dispatch(opsApi.util.resetApiState());
		// Fire role-specific logout event BEFORE resetAnalytics clears the
		// Mixpanel user so it's still attributed to the outgoing user.
		switch (currentRole) {
			case 'student':
				analytics.student.logoutCompleted();
				break;
			case 'trainer':
				analytics.trainer.logoutCompleted();
				break;
			case 'parent':
				analytics.parent.logoutCompleted();
				break;
			case 'school_admin':
				analytics.schoolAdmin.logoutCompleted();
				break;
			case 'master_admin':
				analytics.masterAdmin.logoutCompleted();
				break;
			default:
				break;
		}
		resetAnalytics();

		try {
			if (refreshToken) await logoutApi({ refreshToken }).unwrap();
		} catch {
			/* silent */
		} finally {
			isLoggingOut = false;
		}
	}, [dispatch, logoutApi, role, navigate]);

	return {
		user,
		role,
		loading: false, // Redux hydrates synchronously — no loading state needed
		login,
		logout,
		isAuthenticated,
		mustChangePassword,
		completedSetup,
	};
}

interface ProtectedRouteProps {
	children: ReactNode;
	allowedRoles?: AuthRole[];
	requiresSetup?: boolean;
}

export function ProtectedRoute({
	children,
	allowedRoles,
	requiresSetup = false,
}: ProtectedRouteProps) {
	const { isAuthenticated, role, mustChangePassword } = useAuth();
	const navigate = useNavigate();

	useEffect(() => {
		if (!isAuthenticated) {
			// Skip redirect during logout — logout() already navigated to the
			// role-specific login page; redirecting to '/' would override it.
			if (isLoggingOut) return;
			navigate('/');
			return;
		}
		if (allowedRoles && !allowedRoles.includes(role!)) {
			navigate('/');
			return;
		}

		if (role === 'school_admin') {
			if (requiresSetup && !mustChangePassword) {
				navigate('/school/dashboard');
			} else if (!requiresSetup && mustChangePassword) {
				navigate('/school/setup');
			}
		}

		if (role === 'parent' && !requiresSetup && mustChangePassword) {
			navigate('/parent/change-password');
		}

		if (
			(role === 'salesperson' ||
				role === 'sales_manager' ||
				role === 'appointment_coordinator') &&
			!requiresSetup &&
			mustChangePassword
		) {
			navigate('/sales/change-password');
		}

		// Zonal Manager sets their own password on first login, same as the
		// School Admin / Parent flow. `requiresSetup` marks the change-password
		// route itself — without that check this redirects the page to itself
		// and the guard below blanks it.
		if (role === 'zonal_manager' && !requiresSetup && mustChangePassword) {
			navigate('/zonal/change-password');
		}
	}, [
		isAuthenticated,
		role,
		allowedRoles,
		mustChangePassword,
		requiresSetup,
		navigate,
	]);

	if (!isAuthenticated) return null;
	if (allowedRoles && !allowedRoles.includes(role!)) return null;
	if (role === 'school_admin' && requiresSetup && !mustChangePassword)
		return null;
	if (role === 'school_admin' && !requiresSetup && mustChangePassword)
		return null;
	if (role === 'parent' && !requiresSetup && mustChangePassword) return null;
	if (
		(role === 'salesperson' || role === 'appointment_coordinator') &&
		!requiresSetup &&
		mustChangePassword
	)
		return null;
	if (role === 'zonal_manager' && !requiresSetup && mustChangePassword)
		return null;

	return <>{children}</>;
}
